import CopyPasteProductPricing from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricingGrid/CopyPasteProductPricing'
import {
  createGetCellClassName,
  createGridValueFormatter
} from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricingGrid/pricingGridFormatters'
import {
  addExistingExchangePeriods,
  addExistingPricings,
  createRows
} from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricingGrid/pricingGridRowUtils'
import { addUniqueTitleToPricingPlans } from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricingGrid/pricingPlanTitleUtils'
import {
  type PricingPlanMutationContext,
  updateOrCreatePricingExchangePeriod,
  updateOrCreatePricingPlan
} from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricingGrid/pricingPlanUtils'
import SpecialZonePricing from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricingGrid/SpecialZonePricing'
import StyledDataGrid, {
  DATA_GRID_BORDER_WIDTH
} from 'Services/ProductManagement/Features/Product/Components/ProductPricing/ProductPricingGrid/StyledProductPricingGrid'
import type { RowType } from 'Services/ProductManagement/Features/Product/Components/ProductPricing/types'
import {
  type PricingPlan,
  PricingTabType,
  type Product,
  type Validity
} from 'Services/ProductManagement/Features/Product/types'
import { TariffContext } from 'Services/ProductManagement/Features/Tariff/Context/tariffContext'
import type { ZoneDefinition } from 'Services/ProductManagement/Features/ZoneDefinition/types'
import { useGridHighlight } from 'Util/Context/gridHighlightContext'
import { useUnsavedChanges } from 'Util/Context/unsavedChangesContext'
import {
  EXTENSION_TICKET_GROUP_CODE,
  IS_HSL_SERVICE_ENVIRONMENT,
  IS_TARIFF_EDIT_ALLOWED
} from 'Util/constants'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import { Box, Button, LinearProgress, Skeleton } from '@mui/material'
import { type GridCellParams, type GridColDef, useGridApiRef } from '@mui/x-data-grid'
import { useContext, useEffect, useState } from 'react'
import { t } from 'ttag'

const TITLE_COL_WIDTH = 50
const ROW_HEIGHT = 40
const COL_WIDTH = 150

const IsCellEditedClassName = 'IsEdited'
const IsCellUpdatedClassName = 'IsUpdated'

function CustomNoRowsOverlay(rows: Readonly<string[]>) {
  return (
    <>
      <LinearProgress />
      {rows.map(row => (
        <Skeleton key={`skeleton-row-${row}`} height={ROW_HEIGHT} animation="wave" />
      ))}
    </>
  )
}

interface ProductPricingGridProps {
  pricingPlans: PricingPlan[]
  zoneDefinition: ZoneDefinition
  product: Product
  handleOpenActivePeriod: () => void
  setProductPricingPlans: React.Dispatch<React.SetStateAction<PricingPlan[]>>
  activePeriod: Validity | null
  pricingGridType: PricingTabType
  domicileClass?: string
}

function ProductPricingGrid({
  pricingPlans,
  zoneDefinition,
  product,
  handleOpenActivePeriod,
  setProductPricingPlans,
  activePeriod,
  pricingGridType,
  domicileClass
}: Readonly<ProductPricingGridProps>) {
  const apiRef = useGridApiRef()
  const { markEdited } = useUnsavedChanges()
  const { editedCells, setEditedCells, updatedCells } = useGridHighlight()
  const allZones = Array.from(new Set(zoneDefinition.zones.flat()))
  const [rows, setRows] = useState<RowType[]>([])
  const { copiedPricingPlans, setCopiedPricingPlans } = useContext(TariffContext)
  const isOriginalPricingPlan = activePeriod == null

  const defaultPricingPlans = getDefaultPricingPlans(pricingPlans)

  const [currentPricingPlans, setCurrentPricingPlans] = useState<PricingPlan[]>(defaultPricingPlans)

  const specialZonePricingPlans = pricingPlans.filter(
    pricingPlan =>
      // Filter by the current domicile class (or null if not set)
      matchPricingPlanByCurrentDomicileClass(pricingPlan) &&
      // Only include the pricing plans which belong to special_zones.
      zoneDefinition.special_zones.some(zone => pricingPlan.zone_map.join('') === zone.join(''))
  )
  const [currentSpecialZonePricingPlans, setCurrentSpecialZonePricingPlans] =
    useState<PricingPlan[]>(specialZonePricingPlans)

  function getDefaultPricingPlans(pricingPlans: PricingPlan[]) {
    return pricingPlans.filter(
      pricingPlan =>
        // Filter by the current domicile class (or null if not set)
        matchPricingPlanByCurrentDomicileClass(pricingPlan) &&
        // Filter out the pricing plans which belong to special_zones.
        !zoneDefinition.special_zones.some(zone => pricingPlan.zone_map.join('') === zone.join(''))
    )
  }

  /** Matches pricing plan by the current domicile class or null if domicileClass is not set */
  function matchPricingPlanByCurrentDomicileClass(pricingPlan: PricingPlan) {
    const isDomicileClassMatch = domicileClass
      ? pricingPlan.domicile_class === domicileClass
      : pricingPlan.domicile_class === null
    return isDomicileClassMatch
  }

  // ROW_HEIGHT is multiplied by the number of zones + 1 to account for the title row.
  const dataGridContainerHeight = ROW_HEIGHT * (allZones.length + 1) + DATA_GRID_BORDER_WIDTH * 2

  const isExchangePeriodTab = pricingGridType === PricingTabType.EXCHANGE_PERIOD

  /**
   * Determines whether cells should be disabled by default for the current tab.
   *
   * In HSL environment:
   *   - PRICINGS tab: cells are enabled (empty string) by default
   *   - EXCHANGE_PERIOD tab: cells are disabled (undefined) by default,
   *     only enabled for zone maps that have a pricing plan with a price set
   *
   * In Waltti environment (reversed logic):
   *   - EXCHANGE_PERIOD tab: cells are enabled (empty string) by default
   *   - PRICINGS tab: cells are disabled (undefined) by default,
   *     only enabled for zone maps that have a pricing plan with usage_validity_duration set
   */
  // TODO: Disable pricing grid by default in WALTTI environment when SB has support for empty
  // pricings array in pricing plan
  const isCellDisabledByDefault = IS_HSL_SERVICE_ENVIRONMENT && isExchangePeriodTab

  const mutationContext: PricingPlanMutationContext = {
    activePeriod,
    domicileClass,
    productId: product.id
  }

  const gridValueFormatter = createGridValueFormatter(isExchangePeriodTab)

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: '',
      width: TITLE_COL_WIDTH,
      type: 'string',
      sortable: false,
      headerAlign: 'center',
      align: 'center'
    },
    ...allZones.map(zoneId => ({
      field: zoneId,
      headerName: getTranslation(zoneDefinition.translations[zoneId], 'fi').value,
      width: COL_WIDTH,
      type: 'number' as const,
      editable: true,
      sortable: false,
      headerAlign: 'center' as const,
      flex: 1,
      valueFormatter: gridValueFormatter,
      cellClassName: (params: GridCellParams) => {
        if (params.value === undefined) {
          return 'MuiDataGrid-cell--disabled'
        }
        return ''
      }
    }))
  ]

  const resetRows = () => {
    let newRows = createRows({ zoneDefinition, columns, isCellDisabledByDefault })
    const filteredPlans = getDefaultPricingPlans(pricingPlans)
    newRows = isExchangePeriodTab
      ? addExistingExchangePeriods(newRows, filteredPlans)
      : addExistingPricings(newRows, filteredPlans)
    setRows(newRows)
  }

  useEffect(() => {
    resetRows()
    const updatedDefaultPricingPlans = getDefaultPricingPlans(pricingPlans)
    setCurrentPricingPlans(updatedDefaultPricingPlans)
  }, [pricingPlans])

  const isExtensionTicket = product.product_group === EXTENSION_TICKET_GROUP_CODE

  const filterSpecialZonePricingPlans = (
    zoneDefinition: ZoneDefinition,
    pricingPlan: PricingPlan
  ) => {
    return !zoneDefinition.special_zones.some(
      zone => pricingPlan.zone_map.join('') === zone.join('')
    )
  }

  /**
   * Handles changing the price of a special zone pricing plan or creating a new one if it does not exist.
   *
   * @param zoneMap
   * @param price
   * @param specialZonePricingPlans
   */
  const handleSpecialZonePricingPlanPriceChange = (
    zoneMap: string[],
    price: string,
    specialZonePricingPlans: PricingPlan[]
  ) => {
    let currentSpecialZonePricingPlans: PricingPlan[] = [...specialZonePricingPlans]

    const specialZoneMutationContext = { ...mutationContext, isSpecialZone: true }
    if (isExchangePeriodTab) {
      // Update or create pricing plan for exchange period
      currentSpecialZonePricingPlans = updateOrCreatePricingExchangePeriod(
        zoneMap,
        price,
        currentSpecialZonePricingPlans,
        specialZoneMutationContext
      )
    } else {
      currentSpecialZonePricingPlans = updateOrCreatePricingPlan(
        zoneMap,
        price,
        currentSpecialZonePricingPlans,
        specialZoneMutationContext
      )
    }

    setCurrentSpecialZonePricingPlans(currentSpecialZonePricingPlans)
    setProductPricingPlans(prevState => {
      let otherPricingPlans: PricingPlan[] = []
      // Filter out the pricing plans that are not in special zones
      if (isOriginalPricingPlan) {
        otherPricingPlans = prevState.filter(
          pricingPlan =>
            pricingPlan.active_period !== activePeriod ||
            pricingPlan.domicile_class !== (domicileClass ?? null) ||
            filterSpecialZonePricingPlans(zoneDefinition, pricingPlan)
        )
      } else {
        otherPricingPlans = prevState.filter(
          pricingPlan =>
            pricingPlan.active_period?.title !== activePeriod.title ||
            pricingPlan.domicile_class !== (domicileClass ?? null) ||
            filterSpecialZonePricingPlans(zoneDefinition, pricingPlan)
        )
      }

      markEdited()
      // Combine the updated special zone pricing plans with the other pricing plans
      return [...otherPricingPlans, ...currentSpecialZonePricingPlans]
    })
  }

  const handleCopyPricingPlans = () => {
    setCopiedPricingPlans([...currentPricingPlans, ...currentSpecialZonePricingPlans])
    SnackbarUtils.info(t`Cells copied.`)
  }

  const handlePastePricingPlans = () => {
    if (copiedPricingPlans) {
      // Set domicile_class to match current domicileClass
      // Also set active_period to match the current grid's activePeriod
      const pricingPlansWithActivePeriodAndDomicileClass = copiedPricingPlans.map(plan => ({
        ...plan,
        domicile_class: domicileClass ?? null,
        active_period: activePeriod
      }))

      const updatedPricingPlansWithNewTitle = addUniqueTitleToPricingPlans(
        pricingPlansWithActivePeriodAndDomicileClass,
        product.id
      )

      pastePricingPlansWithoutSpecialZones(updatedPricingPlansWithNewTitle)

      if (isExtensionTicket) {
        pastePricingPlansWithSpecialZones(pricingPlansWithActivePeriodAndDomicileClass)
      }

      // Highlight all pasted cells in the grid
      const pastedCells: RowType[] = copiedPricingPlans.map(plan => ({
        id: plan.zone_map[0],
        field: plan.zone_map[plan.zone_map.length - 1],
        title: columns.find(col => col.field === plan.zone_map[0])?.headerName ?? '',
        gridId: apiRef.current.instanceId.id
      }))

      setEditedCells(prev => [...prev, ...pastedCells])
      markEdited()
    }
  }

  const pastePricingPlansWithoutSpecialZones = (updatedPricingPlansWithNewTitle: PricingPlan[]) => {
    // Filter out the pricing plans that are not in special zones.
    // Special zones are only copied for extension tickets.
    const pricingPlansWithoutSpecialZones = updatedPricingPlansWithNewTitle.filter(
      pricingPlan =>
        !zoneDefinition.special_zones.some(zone => pricingPlan.zone_map.join('') === zone.join(''))
    )

    setCurrentPricingPlans(pricingPlansWithoutSpecialZones)
    // Replace only the pricing plans which have same active period and same domicile class
    // as the copied pricing plans
    setProductPricingPlans(prevState => [
      ...prevState.filter(
        plan =>
          plan.active_period?.title !== activePeriod?.title ||
          plan.domicile_class !== (domicileClass ?? null)
      ),
      ...pricingPlansWithoutSpecialZones.filter(
        pricingPlan =>
          pricingPlan.active_period === activePeriod &&
          pricingPlan.domicile_class === (domicileClass ?? null)
      )
    ])
  }

  const pastePricingPlansWithSpecialZones = (
    pricingPlansWithCorrectDomicileClass: PricingPlan[]
  ) => {
    // Filter out the pricing plans that are in special zones.
    // When copying pricing plans for an extension ticket,
    // we also want to copy the special zones pricing plans
    const pricingPlansWithSpecialZones = pricingPlansWithCorrectDomicileClass.filter(pricingPlan =>
      zoneDefinition.special_zones.some(zone => pricingPlan.zone_map.join('') === zone.join(''))
    )

    const updatedSpecialZonePricingPlansWithNewTitle = addUniqueTitleToPricingPlans(
      pricingPlansWithSpecialZones,
      product.id
    )

    setCurrentSpecialZonePricingPlans(updatedSpecialZonePricingPlansWithNewTitle)
    setProductPricingPlans(prevState => [
      ...prevState,
      ...updatedSpecialZonePricingPlansWithNewTitle
    ])
  }

  /**
   * Handles the update of a pricing plan cell.
   * This function updates the pricing plans based on the modified row.
   * It finds all zone maps that start with the modified row's ID and updates each relevant zone map
   * with the new value. If the updated pricing plans are the same as the original pricing plans,
   * it does not update the parent component.
   * Otherwise, it updates the parent component with the new pricing plans.
   */
  const handlePricingPlanCellUpdate = (newRow: RowType, newRows: RowType[]) => {
    // Update pricing plans based on the modified row
    let currentPricingPlansClone: PricingPlan[] = [...currentPricingPlans]

    // Find all zone maps that start with this row's ID
    const relevantZoneMaps = zoneDefinition.zones.filter(zoneMap => zoneMap[0] === newRow.id)

    // Update each relevant zone map with the new value
    for (const zoneMap of relevantZoneMaps) {
      const lastZone = zoneMap[zoneMap.length - 1]
      const lastZonePriceValue = newRow[lastZone]

      if (isExchangePeriodTab) {
        // Update or create pricing plan for exchange period
        currentPricingPlansClone = updateOrCreatePricingExchangePeriod(
          zoneMap,
          lastZonePriceValue,
          currentPricingPlansClone,
          mutationContext
        )
      } else {
        currentPricingPlansClone = updateOrCreatePricingPlan(
          zoneMap,
          lastZonePriceValue,
          currentPricingPlansClone,
          mutationContext
        )
      }
    }

    // Update the current pricing plans state
    setCurrentPricingPlans(currentPricingPlansClone)

    const updatedPricingPlans = [...currentPricingPlansClone, ...currentSpecialZonePricingPlans]

    // Check if the updated pricing plans are the same as the original pricing plans
    // If they are the same, we do not need to update the parent component
    if (JSON.stringify(updatedPricingPlans) === JSON.stringify(pricingPlans)) {
      return newRows
    }

    // Update the parent component and append values to original state
    // Replace the pricing plans in the parent component which have
    // the same active period title and same domicile class
    setProductPricingPlans(prevState => {
      if (isOriginalPricingPlan) {
        // Filter out the pricing plans which are not updated
        const originalPricingPlans = prevState.filter(
          plan =>
            plan.active_period !== activePeriod || plan.domicile_class !== (domicileClass ?? null)
        )
        return [...updatedPricingPlans, ...originalPricingPlans]
      } else {
        // Filter out the pricing plans which are not updated
        const originalPricingPlans = prevState.filter(
          plan =>
            plan.active_period?.title !== activePeriod.title ||
            plan.domicile_class !== (domicileClass ?? null)
        )
        return [...updatedPricingPlans, ...originalPricingPlans]
      }
    })
    markEdited()
  }

  const getCellClassName = (params: GridCellParams) =>
    createGetCellClassName(
      editedCells,
      updatedCells,
      apiRef.current.instanceId.id,
      IsCellEditedClassName,
      IsCellUpdatedClassName
    )(params)

  const isCellEditable = (params: GridCellParams) => {
    return params.value !== undefined && params.value !== '-' && IS_TARIFF_EDIT_ALLOWED
  }

  const updateEditedPricingPlanCells = (newRow: RowType, oldRow: RowType) => {
    const newEditedCell = {
      id: newRow.id,
      // Find which field has been edited by comparing all properties between newRow and oldRow
      field: Object.keys(newRow).find(key => newRow[key] !== oldRow[key]) || '',
      title: newRow.title,
      gridId: apiRef.current.instanceId.id
    } as RowType

    if (
      newEditedCell.field &&
      !editedCells.some(
        existingCell =>
          existingCell.id === newEditedCell.id &&
          existingCell.field === newEditedCell.field &&
          existingCell.gridId === newEditedCell.gridId
      )
    ) {
      setEditedCells((prevState: RowType[]) => [...prevState, newEditedCell])
    }
  }

  return (
    <Box>
      <Box sx={{ height: dataGridContainerHeight }}>
        <StyledDataGrid
          apiRef={apiRef}
          hideFooter
          disableColumnFilter={true}
          rowHeight={ROW_HEIGHT}
          columnHeaderHeight={ROW_HEIGHT}
          showCellVerticalBorder
          showColumnVerticalBorder
          processRowUpdate={(newRow, oldRow) => {
            setRows(prevRows => {
              const newRows = [...prevRows]
              const rowIndex = newRows.findIndex(row => row.id === newRow.id)
              newRows[rowIndex] = newRow as RowType

              handlePricingPlanCellUpdate(newRow as RowType, newRows)
              return newRows
            })

            updateEditedPricingPlanCells(newRow as RowType, oldRow as RowType)
            return newRow
          }}
          getCellClassName={getCellClassName}
          disableColumnMenu={true}
          isCellEditable={isCellEditable}
          onCellClick={(params: GridCellParams) => {
            if (params.cellMode === 'edit') return
            // Only start edit mode if the cell is editable
            if (isCellEditable(params)) {
              apiRef.current.startCellEditMode({
                id: params.id,
                field: params.field
              })
            }
          }}
          disableRowSelectionOnClick={true}
          columns={columns}
          rows={rows}
          slots={{
            noRowsOverlay: () => CustomNoRowsOverlay(allZones)
          }}
        />
      </Box>
      {isExtensionTicket && (
        <SpecialZonePricing
          zoneDefinition={zoneDefinition}
          product={product}
          pricingPlans={pricingPlans}
          activePeriod={activePeriod}
          pricingGridType={pricingGridType}
          domicileClass={domicileClass}
          currentSpecialZonePricingPlans={currentSpecialZonePricingPlans}
          handleSpecialZonePricingPlanPriceChange={handleSpecialZonePricingPlanPriceChange}
        />
      )}
      {IS_TARIFF_EDIT_ALLOWED && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            justifyContent: isOriginalPricingPlan ? 'space-between' : 'flex-end',
            columnGap: 2
          }}
        >
          <Button
            size="medium"
            variant="text"
            startIcon={<AddIcon />}
            onClick={handleOpenActivePeriod}
          >
            {isExchangePeriodTab ? t`Add exchange period exception` : t`Add pricing exception`}
          </Button>
          <CopyPasteProductPricing
            onCopyPricingPlans={handleCopyPricingPlans}
            onPastePricingPlans={handlePastePricingPlans}
            copiedPricingPlansExist={copiedPricingPlans !== undefined}
            domicileClass={domicileClass}
          />
        </Box>
      )}
    </Box>
  )
}

export default ProductPricingGrid
